import { useCallback } from 'react';

import { GQLResponsePerimeterXChallengeError } from '@change-corgi/core/gqlErrors';
import type { TrackingProperties } from '@change-corgi/tracking-properties';

import { PerimeterXRechallengeError } from '#src/app/shared/utils/perimeterx';

import { usePerimeterXCaptcha } from '../captcha';
import { usePerimeterXTracking } from '../usePerimeterXTracking';

type WithPerimeterXCaptcha = <T>(request: () => Promise<T>, properties?: TrackingProperties) => Promise<T>;

/**
 * For requests to a route in the router's px_enforced_routes, which PerimeterX can answer with a
 * challenge instead of data. Runs `request`, and on a challenge opens the captcha and retries once
 * if it's solved. An unsolved (or abandoned) challenge rethrows the original error so callers fall
 * through to their normal error handling, and a challenged retry throws a PerimeterXRechallengeError.
 *
 * `form` identifies the flow in the px_captcha_* analytics, matching the convention used by the
 * other protected flows (corgi_promote, corgi_sign, corgi_ambassadors_contract, ...). `properties`
 * (e.g. the link) is added to the same events.
 */
export function useWithPerimeterXCaptcha(form: string): WithPerimeterXCaptcha {
	const { trackShow, trackSuccess, trackFailure } = usePerimeterXTracking(form);
	const triggerCaptcha = usePerimeterXCaptcha();

	return useCallback(
		async <T>(request: () => Promise<T>, properties?: TrackingProperties): Promise<T> => {
			try {
				return await request();
			} catch (error) {
				if (!(error instanceof GQLResponsePerimeterXChallengeError)) throw error;

				trackShow(error.operationName, properties);
				const solved = await triggerCaptcha({ pxResponse: error.pxChallenge });
				if (!solved) {
					trackFailure(error.pxChallenge, error.operationName, properties);
					throw error;
				}
				trackSuccess(error.pxChallenge, error.operationName, properties);

				// Only one retry, so a challenge that keeps coming back can't trap the user in a loop.
				try {
					return await request();
				} catch (retryError) {
					if (retryError instanceof GQLResponsePerimeterXChallengeError) {
						throw new PerimeterXRechallengeError(retryError);
					}
					throw retryError;
				}
			}
		},
		[triggerCaptcha, trackShow, trackSuccess, trackFailure],
	);
}
